import React, { useState, useEffect } from 'react';
import { Button, Spinner, Card, Badge } from "react-bootstrap";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth } from '../../utils/AuthContext';
import { updateDataOnRealtimeDb, getDataOnRealtimeDb } from '../../utils/firebase';
import moment from 'moment';

const PayPalSubscription = ({ onSuccess }) => {
    const { currentUser, setSubscription } = useAuth();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPayPal, setShowPayPal] = useState(false);
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);

    useEffect(() => {
        const fetchSubscriptionDetails = async () => {
            try {
                const data = await getDataOnRealtimeDb(`users/${currentUser.uid}/subscription`);
                if (data) {
                    // Check if subscription is expired
                    const expiryDate = moment(data.expiryDate);
                    if (expiryDate.isBefore(moment())) {
                        data.status = 'expired';
                        // Update status in database
                        await updateDataOnRealtimeDb(
                            `users/${currentUser.uid}/subscription`,
                            'status',
                            'expired'
                        );
                        setSubscription('free');
                    }
                    setSubscriptionDetails(data);
                }
            } catch (error) {
                console.error('Error fetching subscription:', error);
            }
        };

        fetchSubscriptionDetails();
    }, [currentUser]);

    const handlePlanSelect = () => {
        setShowPayPal(true);
        setError(null);
    };

    const paypalOptions = {
        "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
        currency: "USD",
        intent: "subscription",
        vault: true
    };

    const handleSubscriptionCreate = (data, actions) => {
        return actions.subscription.create({
            'plan_id': process.env.REACT_APP_PAYPAL_PLAN_ID,
            'application_context': {
                'shipping_preference': 'NO_SHIPPING'
            }
        });
    };

    const handleApprove = async (data) => {
        setLoading(true);
        setError(null);

        try {
            const startDate = new Date();
            const expiryDate = new Date(startDate);
            expiryDate.setMonth(expiryDate.getMonth() + 1);

            const subscriptionData = {
                subscriptionId: data.subscriptionID,
                status: 'active',
                startDate: startDate.toISOString(),
                expiryDate: expiryDate.toISOString(),
                planId: process.env.REACT_APP_PAYPAL_PLAN_ID,
                lastUpdated: new Date().toISOString(),
            };

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}`,
                'subscription',
                subscriptionData
            );

            setSubscription('premium');
            if (onSuccess) {
                onSuccess(subscriptionData);
            }
        } catch (error) {
            console.error('Subscription error:', error);
            setError('Failed to activate subscription. Please try again.');
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleError = (err) => {
        console.error('PayPal error:', err);
        setError('Payment failed. Please try again or contact support.');
        setLoading(false);
    };

    const handleCancel = () => {
        setError('Payment was cancelled. Please try again if you want to subscribe.');
        setLoading(false);
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.')) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Update subscription status to cancelled in database
            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/subscription`,
                'status',
                'cancelled'
            );

            await updateDataOnRealtimeDb(
                `users/${currentUser.uid}/subscription`,
                'lastUpdated',
                new Date().toISOString()
            );

            // Update local state
            setSubscriptionDetails(prev => ({
                ...prev,
                status: 'cancelled'
            }));

            setSubscription('free');
            
            // Show success message
            setError('Subscription cancelled successfully. You will retain access to premium features until your current billing period ends.');
            
        } catch (error) {
            console.error('Cancel subscription error:', error);
            setError('Failed to cancel subscription. Please try again or contact support.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            active: { bg: 'success', text: 'Active' },
            expired: { bg: 'danger', text: 'Expired' },
            cancelled: { bg: 'warning', text: 'Cancelled' }
        };
        const config = statusConfig[status] || statusConfig.expired;
        
        return (
            <Badge bg={config.bg} className="ms-2">
                {config.text}
            </Badge>
        );
    };

    const getDaysRemaining = () => {
        if (!subscriptionDetails?.expiryDate) return 0;
        return Math.max(0, moment(subscriptionDetails.expiryDate).diff(moment(), 'days'));
    };

    if (loading) {
        return (
            <div className="text-center p-3">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="text-muted small mt-2 mb-0">Processing your subscription...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center">
                <p className="text-danger small mb-3">{error}</p>
                <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => {
                        setError(null);
                        setShowPayPal(false);
                    }}
                >
                    Try Again
                </Button>
            </div>
        );
    }

    // Show subscription details if exists and not trying to renew
    if (subscriptionDetails && !showPayPal) {
        return (
            <Card className="subscription-details mt-3">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                            Premium Subscription
                            {getStatusBadge(subscriptionDetails.status)}
                        </h6>
                    </div>

                    <div className="subscription-info">
                        <div className="info-item mb-2">
                            <small className="text-muted d-block">Start Date</small>
                            <span>{moment(subscriptionDetails.startDate).format('MMM DD, YYYY')}</span>
                        </div>
                        <div className="info-item mb-2">
                            <small className="text-muted d-block">Expiry Date</small>
                            <span>{moment(subscriptionDetails.expiryDate).format('MMM DD, YYYY')}</span>
                        </div>
                        {subscriptionDetails.status === 'active' && (
                            <div className="info-item mb-2">
                                <small className="text-muted d-block">Days Remaining</small>
                                <span>{getDaysRemaining()} days</span>
                            </div>
                        )}
                        <div className="info-item">
                            <small className="text-muted d-block">Subscription ID</small>
                            <span className="text-monospace">{subscriptionDetails.subscriptionId}</span>
                        </div>
                    </div>

                    {subscriptionDetails.status === 'active' && (
                        <div className="mt-3">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="w-100"
                                onClick={handleCancelSubscription}
                                disabled={loading}
                            >
                                <i className="ri-close-circle-line me-1"></i>
                                Cancel Subscription
                            </Button>
                            <small className="text-muted d-block mt-2 text-center">
                                You'll retain access until {moment(subscriptionDetails.expiryDate).format('MMM DD, YYYY')}
                            </small>
                        </div>
                    )}

                    {subscriptionDetails.status !== 'active' && (
                        <div className="mt-3">
                            <p className="text-danger small mb-2">
                                Your subscription has {subscriptionDetails.status}. 
                                Renew now to continue enjoying premium features.
                            </p>
                            <Button
                                variant="primary"
                                size="sm"
                                className="w-100"
                                onClick={() => {
                                    setShowPayPal(true);
                                    setError(null);
                                }}
                            >
                                <i className="ri-refresh-line me-1"></i>
                                Renew Subscription
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    }

    return (
        <div className="paypal-subscription mt-3">
            {showPayPal ? (
                <PayPalScriptProvider options={paypalOptions}>
                    <PayPalButtons
                        createSubscription={handleSubscriptionCreate}
                        onApprove={handleApprove}
                        onError={handleError}
                        onCancel={handleCancel}
                        style={{
                            shape: 'rect',
                            color: 'blue',
                            layout: 'vertical',
                            label: 'subscribe'
                        }}
                    />
                    <button 
                        className="btn btn-link btn-sm d-block w-100 mt-2"
                        onClick={() => setShowPayPal(false)}
                    >
                        <i className="ri-arrow-left-line me-1"></i>
                        Back
                    </button>
                </PayPalScriptProvider>
            ) : (
                <Button
                    variant="primary"
                    className="w-100"
                    onClick={handlePlanSelect}
                >
                    <i className="ri-paypal-line me-2"></i>
                    Subscribe with PayPal
                </Button>
            )}
        </div>
    );
};

export default PayPalSubscription; 